import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AuthContext from "../context/AuthProvider";
import { startRegistration } from '@simplewebauthn/browser';


const PROFILE_URL = `${process.env.REACT_APP_API_URL}/account/profile`;
const CHANGE_PASSWORD_URL = `${process.env.REACT_APP_API_URL}/account/password/change`;
const PASSKEYS_CREDENCIALES_URL = `${process.env.REACT_APP_API_URL}/auth/webauthn/credenciales`;
const PASSKEYS_REGISTRO_OPTIONS_URL = `${process.env.REACT_APP_API_URL}/auth/webauthn/registro/options`;
const PASSKEYS_REGISTRO_VERIFY_URL = `${process.env.REACT_APP_API_URL}/auth/webauthn/registro/verify`;

const IconoOjo = ({ visible }) => {
  if (visible) {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
        <path
          fill="currentColor"
          d="M2.1 3.51L3.51 2.1 21.9 20.49 20.49 21.9l-2.16-2.16A11.7 11.7 0 0 1 12 21C6 21 2.73 16.89 1 12c.7-2.02 1.77-3.82 3.13-5.31L2.1 3.51ZM12 7c6 0 9.27 4.11 11 9a15.63 15.63 0 0 1-2.93 4.57l-1.43-1.43A13.47 13.47 0 0 0 21 16c-1.73-4.89-5-9-9-9-1.03 0-2 .22-2.9.6L7.74 6.24C9.05 5.48 10.49 5 12 5c1.77 0 3.4.45 4.86 1.19l-1.45 1.45A6.97 6.97 0 0 0 12 7Zm0 2a4.98 4.98 0 0 1 4.12 2.17l-1.44 1.44A3 3 0 0 0 12 11a3 3 0 0 0-1.61.47L8.94 10.02A4.95 4.95 0 0 1 12 9Zm0 6a3 3 0 0 0 1.53-.42l-3.95-3.95A3 3 0 0 0 12 15Z"
        />
      </svg>
    );
  }

  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 5c6 0 9.27 4.11 11 9-1.73 4.89-5 9-11 9S2.73 18.89 1 14c1.73-4.89 5-9 11-9Zm0 2c-4 0-7.27 4.11-9 7 1.73 2.89 5 7 9 7s7.27-4.11 9-7c-1.73-2.89-5-7-9-7Zm0 2a5 5 0 1 1 0 10 5 5 0 0 1 0-10Zm0 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z"
      />
    </svg>
  );
};

export function ProfilePage() {
  const { auth, setAuth } = useContext(AuthContext);
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [profileError, setProfileError] = useState("");
  const [loadingProfile, setLoadingProfile] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [passwordMessage, setPasswordMessage] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [mostrarPasswordActual, setMostrarPasswordActual] = useState(false);
  const [mostrarPasswordNueva, setMostrarPasswordNueva] = useState(false);
  const [mostrarPasswordRepetir, setMostrarPasswordRepetir] = useState(false);
  
  const [passkeysInfo, setPasskeysInfo] = useState({ cantidad: 0, credenciales: [] });
  const [passkeysLoading, setPasskeysLoading] = useState(false);
  const [passkeysError, setPasskeysError] = useState('');
  const [passkeysDisponible, setPasskeysDisponible] = useState(false);

  const estiloContenedorPassword = {
    position: "relative",
    display: "block"
  };

  const estiloOjo = {
    position: "absolute",
    right: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "#666666",
    zIndex: 2
  };

  const tokenFromAuth = auth?.token;
  const tokenFromStorage = JSON.parse(
    window.localStorage.getItem("auth") || "{}"
  )?.token;
  const token = tokenFromAuth || tokenFromStorage;

  const logout = () => {
    setAuth({});
    window.localStorage.removeItem("auth");
    navigate("/login");
  };

    useEffect(() => {
    const check = async () => {
      try {
        const ok = await window.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable?.();
        setPasskeysDisponible(!!ok);
      } catch (e) {
        setPasskeysDisponible(false);
      }
    };
    check();
  }, []);

    const fetchPasskeys = async () => {
    if (!token) return;

    try {
      setPasskeysLoading(true);
      setPasskeysError('');

      const res = await axios.get(PASSKEYS_CREDENCIALES_URL, {
        headers: { Authorization: token.toString() }
      });

      setPasskeysInfo(res?.data || { cantidad: 0, credenciales: [] });
    } catch (err) {
      const msg = err?.response?.data?.message || 'Error al consultar credenciales.';
      setPasskeysError(msg);
    } finally {
      setPasskeysLoading(false);
    }
  };

  useEffect(() => {
    fetchPasskeys();
  }, [token]);

  const activarPasskey = async () => {
    if (!token) return;

    try {
      setPasskeysLoading(true);
      setPasskeysError('');

      const opt = await axios.get(PASSKEYS_REGISTRO_OPTIONS_URL, {
        headers: { Authorization: token.toString() }
      });

      const desafio_id = opt?.data?.desafio_id;
      const options = opt?.data?.options;

      if (!desafio_id || !options) {
        setPasskeysError('No se pudo iniciar el registro de huella.');
        return;
      }

      const attResp = await startRegistration(options);

      await axios.post(
        PASSKEYS_REGISTRO_VERIFY_URL,
        JSON.stringify({ desafio_id, response: attResp }),
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: token.toString()
          }
        }
      );

      await fetchPasskeys();
    } catch (err) {
      const msg = err?.response?.data?.message || 'No se pudo activar el acceso con pin/huella.';
      setPasskeysError(msg);
    } finally {
      setPasskeysLoading(false);
    }
  };

  const desactivarPasskeys = async () => {
    if (!token) return;

    try {
      setPasskeysLoading(true);
      setPasskeysError('');

      await axios.delete(PASSKEYS_CREDENCIALES_URL, {
        headers: { Authorization: token.toString() }
      });

      await fetchPasskeys();
    } catch (err) {
      const msg = err?.response?.data?.message || 'No se pudo desactivar el acceso con pin/huella.';
      setPasskeysError(msg);
    } finally {
      setPasskeysLoading(false);
    }
  };



  // Cargar perfil
  useEffect(() => {
    const fetchProfile = async () => {
      if (!token) {
        setProfileError("Token no disponible");
        console.error("ProfilePage: token no disponible");
        return;
      }

      try {
        setLoadingProfile(true);
        setProfileError("");
        const res = await axios.get(PROFILE_URL, {
          headers: { Authorization: token.toString() },
        });
        setProfile(res.data);
      } catch (err) {
        const msg =
          err?.response?.data?.message || "Error al obtener el perfil";
        setProfileError(msg);
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchProfile();
  }, [token]);

  const requiresPasswordChange =
    !!profile?.password?.requiere_cambio || !!profile?.password?.expirado;
  const diasRestantes = profile?.password?.dias_restantes;

  const handleChangePassword = async (e) => {
    e.preventDefault();

    setPasswordError("");
    setPasswordMessage("");

    if (!currentPassword || !newPassword || !repeatPassword) {
      setPasswordError("Todos los campos de contraseña son obligatorios.");
      return;
    }

    if (currentPassword === newPassword) {
      setPasswordError("La nueva contraseña no puede ser igual a la contraseña actual.");
      return;
    }

    if (newPassword !== repeatPassword) {
      setPasswordError("La nueva contraseña y su repetición no coinciden.");
      return;
    }

    if (newPassword.length < 8 || repeatPassword.length < 8) {
      setPasswordError("La nueva contraseña debe contener un mínimo de 8 caracteres.");
      return;
    }

    if (!token) {
      setPasswordError("Token no disponible.");
      return;
    }

    try {
      setPasswordSaving(true);

      const payload = {
        password_old: currentPassword,
        password_new: newPassword,
      };

      const res = await axios.post(
        CHANGE_PASSWORD_URL,
        JSON.stringify(payload),
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: token.toString(),
          },
        }
      );

      const msg = res?.data?.message || "Contraseña cambiada correctamente";
      setPasswordMessage(msg);

      setTimeout(() => {
        logout();
      }, 1500);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        "Ocurrió un error al cambiar la contraseña.";
      setPasswordError(msg);

      if (msg.toLowerCase().includes("bloqueado")) {
        setTimeout(() => {
          logout();
        }, 1500);
      }
    } finally {
      setPasswordSaving(false);
    }
  };

  const renderPasswordWarning = () => {
    if (!requiresPasswordChange) return null;

    const expirado = profile?.password?.expirado;
    let text = "";

    if (expirado) {
      text =
        "Tu contraseña está vencida. Debés renovarla para continuar utilizando el sistema.";
    } else if (typeof diasRestantes === "number") {
      if (diasRestantes <= 0) {
        text =
          "Tu contraseña está vencida. Debés renovarla para continuar utilizando el sistema.";
      } else {
        text = `Cambio de contraseña requerido. Días restantes: ${diasRestantes}.`;
      }
    } else {
      text = "Cambio de contraseña requerido.";
    }

    return (
      <div style={{ marginTop: "22px" }}>
        <hr className="perfil-separator" />

        <p
          className="perfil-ui-font"
          style={{
            fontWeight: "bold",
            color: "#d9a300",
            marginBottom: "12px",
          }}
        >
          {text}
        </p>

        <form onSubmit={handleChangePassword} className="perfil-pass-form">
          <div className="form-group">
            <div style={estiloContenedorPassword}>
              <input
                type={mostrarPasswordActual ? "text" : "password"}
                className="form-text perfil-input"
                placeholder="Contraseña actual"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                style={{ paddingRight: "44px" }}
              />

              <span
                role="button"
                tabIndex={0}
                style={estiloOjo}
                onClick={() => setMostrarPasswordActual(!mostrarPasswordActual)}
                onKeyDown={(e)=>{
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setMostrarPasswordActual(!mostrarPasswordActual);
                  }
                }}
                aria-label={mostrarPasswordActual ? "Ocultar contraseña actual" : "Mostrar contraseña actual"}
                title={mostrarPasswordActual ? "Ocultar contraseña actual" : "Mostrar contraseña actual"}
              >
                <IconoOjo visible={mostrarPasswordActual} />
              </span>
            </div>
          </div>

          <div className="form-group">
            <div style={estiloContenedorPassword}>
              <input
                type={mostrarPasswordNueva ? "text" : "password"}
                className="form-text perfil-input"
                placeholder="Nueva contraseña"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                style={{ paddingRight: "44px" }}
                minLength={8}
              />

              <span
                role="button"
                tabIndex={0}
                style={estiloOjo}
                onClick={() => setMostrarPasswordNueva(!mostrarPasswordNueva)}
                onKeyDown={(e)=>{
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setMostrarPasswordNueva(!mostrarPasswordNueva);
                  }
                }}
                aria-label={mostrarPasswordNueva ? "Ocultar nueva contraseña" : "Mostrar nueva contraseña"}
                title={mostrarPasswordNueva ? "Ocultar nueva contraseña" : "Mostrar nueva contraseña"}
              >
                <IconoOjo visible={mostrarPasswordNueva} />
              </span>
            </div>
          </div>

          <div className="form-group">
            <div style={estiloContenedorPassword}>
              <input
                type={mostrarPasswordRepetir ? "text" : "password"}
                className="form-text perfil-input"
                placeholder="Repetir nueva contraseña"
                value={repeatPassword}
                onChange={(e) => setRepeatPassword(e.target.value)}
                style={{ paddingRight: "44px" }}
                minLength={8}
              />

              <span
                role="button"
                tabIndex={0}
                style={estiloOjo}
                onClick={() => setMostrarPasswordRepetir(!mostrarPasswordRepetir)}
                onKeyDown={(e)=>{
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setMostrarPasswordRepetir(!mostrarPasswordRepetir);
                  }
                }}
                aria-label={mostrarPasswordRepetir ? "Ocultar repetición de contraseña" : "Mostrar repetición de contraseña"}
                title={mostrarPasswordRepetir ? "Ocultar repetición de contraseña" : "Mostrar repetición de contraseña"}
              >
                <IconoOjo visible={mostrarPasswordRepetir} />
              </span>
            </div>
          </div>

          {passwordError && (
            <p
              className="perfil-ui-font"
              style={{
                color: "#ff4d4f",
                fontWeight: "bold",
                marginTop: "10px",
                marginBottom: "10px",
              }}
            >
              {passwordError}
            </p>
          )}

          {passwordMessage && (
            <p
              className="perfil-ui-font"
              style={{
                color: "#0A9E95",
                fontWeight: "bold",
                marginTop: "10px",
                marginBottom: "10px",
              }}
            >
              {passwordMessage}
            </p>
          )}

          <button
            type="submit"
            className="btn-primary perfil-btn-renovar"
            disabled={passwordSaving}
          >
            {passwordSaving ? "Guardando..." : "Renovar contraseña"}
          </button>

          <button
            type="button"
            className="btn-primary perfil-btn-renovar"
            style={{
              backgroundColor: "#666666",
              borderColor: "#666666",
              marginTop: "10px",
            }}
            onClick={() => navigate("/recuperar-contrasena")}
          >
            Olvidé mi contraseña
          </button>
        </form>
      </div>
    );
  };

  const handleBack = () => {
    navigate("/");
  };

  return (
    <div className="body-home">
      {/* Header con logo */}
      <div className="row header-int">
        <div className="col-md-4 d-none d-md-block">
          <p></p>
        </div>
        <div className="col-md-4 col-sm-12 logo-container">
          <p className="text-center">
            <img className="logo" src="/logo.png" alt="Ethical Nutrition" />
          </p>
        </div>
        <div className="col-md-4 d-none d-md-block">
          <p></p>
        </div>
      </div>

      <div className="row">
        <div className="col-md-4 d-none d-md-block">
          <p></p>
        </div>

        <div className="col-md-4 col-sm-12 body-content-int">
          <div className="perfil-header-row">
            <button
              type="button"
              className="btn btn-light btn-back"
              onClick={handleBack}
            >
              &lt;
            </button>

            <h5 className="perfil-title mb-0 flex-grow-1 text-center">
              Información de Usuario
            </h5>

            <div style={{ width: "40px" }} />
          </div>

          <div className="ecard ecard-perfil perfil-card">
            {loadingProfile && <p>Cargando perfil...</p>}

            {profileError && (
              <p
                className="perfil-ui-font"
                style={{ color: "#ff4d4f", fontWeight: "bold" }}
              >
                {profileError}
              </p>
            )}

            {profile && !loadingProfile && !profileError && (
                <>
                  <div className="perfil-info-stack">
                    <div className="perfil-info-block">
                      <div className="perfil-info-label">Médico</div>
                      <div className="perfil-info-value">
                        {profile.medico || "-"}
                      </div>
                    </div>

                    <div className="perfil-info-block">
                      <div className="perfil-info-label">Email</div>
                      <div className="perfil-info-value">
                        {profile.email || "-"}
                      </div>
                    </div>

                    <div className="perfil-info-block">
                      <div className="perfil-info-label">APM</div>
                      <div className="perfil-info-value">
                        {profile.apm || "-"}
                      </div>
                    </div>
                  </div>

                  <hr className="perfil-separator" />

                  <div className="perfil-info-block">
                    <div className="perfil-info-label">Acceso con pin/huella</div>

                    {!passkeysDisponible && (
                      <div className="perfil-info-value" style={{ color: '#666' }}>
                        Tu dispositivo/navegador no soporta acceso con pin/huella para este sitio.
                      </div>
                    )}

                    {passkeysDisponible && (
                      <div
                        className="perfil-info-value"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '12px'
                        }}
                      >
                        <span>
                          {passkeysLoading
                            ? 'Cargando...'
                            : (passkeysInfo?.cantidad > 0 ? 'Activado' : 'Desactivado')}
                        </span>

                        <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <input
                            type="checkbox"
                            checked={passkeysInfo?.cantidad > 0}
                            disabled={passkeysLoading}
                            onChange={async (e) => {
                              if (e.target.checked) await activarPasskey();
                              else await desactivarPasskeys();
                            }}
                          />
                          <span>Usar pin/huella</span>
                        </label>
                      </div>
                    )}

                    {passkeysError && (
                      <div
                        className="perfil-ui-font"
                        style={{
                          color: '#ff4d4f',
                          fontWeight: 'bold',
                          marginTop: '10px'
                        }}
                      >
                        {passkeysError}
                      </div>
                    )}
                  </div>
                  {renderPasswordWarning()}
                </>
              )}
          </div>
        </div>

        <div className="col-md-4 d-none d-md-block">
          <p></p>
        </div>
      </div>
    </div>
  );
}